import {AiTaggingTemplateDefinition} from '@frontapp/workflows/rules';
import React, {FC, useReducer} from 'react';
import styled from 'styled-components';

import {useAppQuery} from '../../../libs/shared/core/src/services/query/useAppQuery';
import {Dialog} from '../../../libs/shared/ui/src/components/dialogs/dialog';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {ConversationAiStatusesEnum} from '../../models/api/conversationAiMarkModel';
import {TagModel} from '../../models/api/tagModel';
import {useApplyAiReviewMutation} from '../../queries/aiTaggingMutations';
import {aiTaggingQueries} from '../../queries/aiTaggingQueries';
import {tagQueries} from '../../queries/tagQueries';
import {getCompany} from '../../store/data/company/companySelectors';
import {AiHarvestingContentHeader} from './aiHarvestingContentHeader';
import {AiHarvestingCta} from './aiHarvestingCta';
import {
  aiHarvestingModalReducer,
  AiHarvestinModalActionTypesEnum,
  initialState,
} from './aiHarvestingModalReducer';
import {AiReviewHarvestingSidebar} from './aiHarvestingSidebar';

/*
 * Props.
 */

const REVIEW_DIALOG_WIDTH = 1084;
const REVIEW_DIALOG_HEIGHT = 692;

interface AiReviewHarvestingModalProps {
  ruleBundleId: number;
  onCancel: () => void;
  aiTaggingDefinition?: AiTaggingTemplateDefinition;
  onReviewSuccessData: (data: AiTaggingTemplateDefinition) => void;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  flex-grow: 1;
  display: grid;
  grid-template-columns: 240px 1fr;
  height: ${REVIEW_DIALOG_HEIGHT}px;
  width: ${REVIEW_DIALOG_WIDTH}px;
  position: relative;
`;

const StyledContentWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: ${REVIEW_DIALOG_HEIGHT}px;
  width: ${REVIEW_DIALOG_WIDTH - 240}px;
  overflow: auto;

  padding: 30px;
  padding-top: 0;
`;

/*
 * Component.
 */

export const AiReviewHarvestingModal: FC<AiReviewHarvestingModalProps> = (props) => {
  const {onCancel, ruleBundleId, aiTaggingDefinition, onReviewSuccessData} = props;

  const company = useAppSelector(getCompany);
  const [state, dispatchAction] = useReducer(aiHarvestingModalReducer, initialState);
  const {currentlySelectedTagIndex, messageIndex, currentCursor, reviewStatus, hasError} = state;

  // Data fetching.
  const {isTagsLoading, tags} = useAiTaggingTags(aiTaggingDefinition);
  const {isReviewLoading, harvestingMessages, nextId, hasMore} = useHarvestingMessages(
    ruleBundleId,
    currentCursor,
    tags[currentlySelectedTagIndex]?.id,
  );

  // When we need to fetch a new page of messages, update the currentCursor so that the react query is triggered.
  if (hasMore && messageIndex >= harvestingMessages.length) {
    dispatchAction({type: AiHarvestinModalActionTypesEnum.GET_NEW_PAGE, payload: nextId});
  }

  // Handlers
  const onTagSelect = (index: number) => {
    dispatchAction({type: AiHarvestinModalActionTypesEnum.SET_CURRENTLY_SELECTED_TAG_INDEX, payload: index});
  };
  const aiReviewMutation = useApplyAiReviewMutation(
    company,
    ruleBundleId,
    (data) => {
      dispatchAction({type: AiHarvestinModalActionTypesEnum.REVIEW_SUCCESS});
      onReviewSuccessData(data);
    },
    () => {
      dispatchAction({type: AiHarvestinModalActionTypesEnum.REVIEW_ERROR});
    },
  );

  const isHarvestingDone = (tag?: TagModel) =>
    aiTaggingDefinition?.training_sets.find((ts) => ts.resource_id === tag?.id)?.harvesting_finished ?? false;

  const selectedTag = tags[currentlySelectedTagIndex];

  const canGoToNextTag = currentlySelectedTagIndex < tags.length - 1;

  return (
    <Dialog width={REVIEW_DIALOG_WIDTH} height={REVIEW_DIALOG_HEIGHT} onRequestClose={onCancel}>
      <StyledWrapperDiv>
        {/* Sidebar */}
        <AiReviewHarvestingSidebar
          isLoading={isTagsLoading}
          reviewItems={tags.map((tag, index) => ({
            tag,
            isHarvestingDone: isHarvestingDone(tag),
            isSelected: currentlySelectedTagIndex === index,
            onSelect: () => onTagSelect(index),
          }))}
        />

        {/* Content */}
        <StyledContentWrapperDiv>
          <AiHarvestingContentHeader
            tag={selectedTag}
            aiTaggingTemplate={aiTaggingDefinition}
            onCancel={onCancel}
          />
          <AiHarvestingCta
            tag={selectedTag}
            reviewStatus={reviewStatus}
            hasError={hasError}
            isLoading={isReviewLoading}
            isHarvestingDone={isHarvestingDone(selectedTag)}
            message={harvestingMessages[messageIndex]}
            canGoToNextTag={canGoToNextTag}
            onNextTagClick={() => {
              dispatchAction({
                type: AiHarvestinModalActionTypesEnum.SET_CURRENTLY_SELECTED_TAG_INDEX,
                payload: currentlySelectedTagIndex + 1,
              });
            }}
            onFinishClick={onCancel}
            onStatusChangeClick={(status: ConversationAiStatusesEnum) => {
              dispatchAction({type: AiHarvestinModalActionTypesEnum.SET_REVIEW_STATUS, payload: status});
              aiReviewMutation.mutate({
                action: status,
                messageId: harvestingMessages[messageIndex].id,
                tagId: selectedTag?.id,
              });
            }}
          />
        </StyledContentWrapperDiv>
      </StyledWrapperDiv>
    </Dialog>
  );
};

/*
 * Hooks.
 */

function useAiTaggingTags(aiTaggingTemplate: AiTaggingTemplateDefinition | undefined): {
  isTagsLoading: boolean;
  tags: ReadonlyArray<TagModel>;
} {
  const tagIds = aiTaggingTemplate?.training_sets.map((ts) => ts.resource_id) ?? [];

  const {isLoading: isTagsLoading, data: tags} = useAppQuery(tagQueries.ids(tagIds));

  return {isTagsLoading, tags: tags ?? []};
}

function useHarvestingMessages(ruleBundleId: number, nextId: number, tagId: number | undefined) {
  const {isFetchedAfterMount: isLoadedAfterMount, data} = useAppQuery(
    aiTaggingQueries.harvestedMessages(ruleBundleId, nextId, tagId),
  );

  return {
    isReviewLoading: !isLoadedAfterMount,
    harvestingMessages: data?.messages ?? [],
    nextId: data?.nextId,
    hasMore: data?.hasMore ?? false,
  };
}
